/**
 * Phone Demo Content
 *
 * This module renders an interactive iPhone mockup showing a preview of the
 * generated SMS journey conversation. Uses real demo data from the API.
 */

import { computeStats, DemoStat, DemoMessage } from './phone-demo-stats.js';
import { ErrorConfig } from './types.js';

interface DemoData {
  conversation: DemoMessage[];
  stats: DemoStat[];
}

interface DemoMetadata {
  demoData?: {
    content?: {
      is_valid?: boolean;
      errors?: string[];
    };
  };
  access?: {
    isFullAccess?: boolean;
    hasAccessKey?: boolean;
  };
}

declare global {
  interface Window {
    phoneDemoContent: typeof phoneDemoContent;
  }
}

const phoneDemoContent = {
  /**
   * Get static stats that are always shown
   */
  getStaticStats: function(): DemoStat[] {
    return [
      { label: 'Smart Timing', text: 'High frequency → tapered engagement' },
      { label: '71% LTV Improvement', text: 'Typical improvement across Blustream clients' },
      { label: '91% Retention', text: 'Customers stay opted in to messaging' }
    ];
  },

  /**
   * Generate the left panel with heading, subheading, and stats
   */
  generateLeftPanel: function(statsHtml: string, productName?: string): string {
    const displayProduct = productName ? this.escapeHtml(productName) : 'Your Product';
    return `
      <div class="w-full md:w-1/2 pr-0 md:pr-8 flex flex-col justify-start p-0 md:p-8 md:h-auto">
        <!-- Main heading -->
        <h2 class="text-6xl font-normal text-gray-900 mb-6">Polly Preview for<br>${displayProduct}</h2>

        <!-- Subheading -->
        <p class="text-lg mb-6">Scroll through to see the conversations Polly, your product's AI Advisor, would deliver on your behalf—in your brand's voice, at the moments that matter most throughout the product lifecycle.</p>

        <!-- What Polly recommends -->
        <p class="text-lg font-semibold mb-4">What Polly recommends for ${displayProduct}:</p>

        <!-- Stats list -->
        ${statsHtml}
      </div>
    `;
  },

  /**
   * Generate the two-column layout with left panel and phone mockup
   */
  generateLayout: function(statsHtml: string, phoneMockupHtml: string, productName?: string): string {
    return `
      <div class="flex flex-col md:flex-row w-full">
        <!-- Left side content (text) -->
        ${this.generateLeftPanel(statsHtml, productName)}

        <!-- Right side content (phone mockup) -->
        <div class="w-full md:w-1/2 mt-8 md:mt-0 flex flex-col items-center justify-center p-4">
          ${phoneMockupHtml}
          <div class="disclaimer-container max-w-[350px]" style="margin-top: 24px;">
            <p class="text-sm text-gray-500 italic text-center">While we do our best, AI can make mistakes. Don't worry, you're in complete control of the messages that go to your customers.</p>
          </div>
        </div>
      </div>
    `;
  },

  /**
   * Generate the phone demo content with stats panel and phone mockup
   */
  generate: function(demoData: DemoData | null, mermaidDiagram: string | null, companyName?: string, isFullAccess: boolean = true, productName?: string): string {
    const conversation = demoData?.conversation || [];
    const aiStats = demoData?.stats || [];

    // Compute programmatic stats from mermaid diagram and conversation
    const programmaticStats = computeStats(mermaidDiagram, conversation);

    // Merge: programmatic stats first, then AI stats, then static stats
    const allStats = [...programmaticStats, ...aiStats, ...this.getStaticStats()];

    // Generate content - show typing indicator for partial access
    const showTypingIndicator = !isFullAccess;
    const messagesHtml = this.generateMessages(conversation, showTypingIndicator);
    const statsHtml = this.generateStatsChips(allStats);
    const phoneMockupHtml = this.generatePhoneMockup(messagesHtml, companyName, showTypingIndicator);

    return this.generateLayout(statsHtml, phoneMockupHtml, productName);
  },

  /**
   * Generate HTML for stats list
   */
  generateStatsChips: function(stats: DemoStat[]): string {
    const checkmarkSvg = `<svg class="h-4 w-4 text-brand-blue" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
      <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round"></path>
    </svg>`;

    return `<ul class="space-y-3">${stats.map(stat => `
      <li class="flex items-start">
        <span class="h-6 flex items-center mr-3">
          ${checkmarkSvg}
        </span>
        <span><strong>${stat.label}:</strong> ${stat.text || ''}</span>
      </li>
    `).join('')}</ul>`;
  },

  /**
   * Generate typing indicator bubble (three animated dots)
   */
  generateTypingIndicator: function(): string {
    return `
      <div class="flex justify-start mb-2">
        <div class="bg-gray-200 text-gray-900 rounded-2xl rounded-bl px-3 py-2">
          <div class="flex items-center gap-1">
            <span class="typing-dot w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse" style="animation-delay: 0ms;"></span>
            <span class="typing-dot w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse" style="animation-delay: 300ms;"></span>
            <span class="typing-dot w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse" style="animation-delay: 600ms;"></span>
          </div>
        </div>
      </div>
    `;
  },

  /**
   * Generate CTA for partial access users
   */
  generatePartialAccessCTA: function(): string {
    return `
      <div class="mt-8 mb-4 text-center">
        <div class="overlay-inner-container inline-block">
          <div class="overlay-message">See the full example by scheduling a meeting with our product experts.</div>
          <button class="brand-button-compact" onclick="window.open('${"https://blustream.io/contact-sales?utm_source=pxai_journey&utm_medium=internal&utm_campaign=13746024-PXAI%20Journey%20Builder"}', '_blank')">Schedule a Meeting</button>
        </div>
      </div>
    `;
  },

  /**
   * Generate HTML for message bubbles
   */
  generateMessages: function(conversation: DemoMessage[], showTypingIndicator: boolean = false): string {
    if (!conversation || !conversation.length) {
      return '<p class="text-gray-400 text-center py-8">No messages to display</p>';
    }

    let html = '';
    let currentLabel: string | null = null;

    for (const msg of conversation) {
      // Add day label if it changed
      if (msg.label && msg.label !== currentLabel && msg.label.trim() !== '') {
        currentLabel = msg.label;
        html += `
          <div class="flex justify-center mt-[16px] mb-[6px]">
            <span class="text-xs font-semibold uppercase" style="color: var(--colors-brand-dark-gray);">${msg.label}</span>
          </div>
        `;
      }

      // Message bubble
      const isBrand = msg.source === 'brand';
      const bubbleClasses = isBrand
        ? 'bg-gray-200 text-gray-900 rounded-2xl rounded-bl self-start'
        : 'bg-blue-500 text-white rounded-2xl rounded-br self-end';

      html += `
        <div class="flex ${isBrand ? 'justify-start' : 'justify-end'} mb-2">
          <div class="${bubbleClasses} px-4 py-2 max-w-[75%] text-xs">
            ${this.linkify(this.escapeHtml(msg.text), isBrand)}
          </div>
        </div>
      `;
    }

    // Add typing indicator for partial access
    if (showTypingIndicator) {
      html += this.generateTypingIndicator();
    }

    return html;
  },

  /**
   * Generate the phone status bar (time, Dynamic Island, icons)
   */
  generatePhoneStatusBar: function(): string {
    // Dynamic Island is 62px wide, centered in 256px screen
    // Left region: from 0 to (256-62)/2 = 97px (but with padding)
    // Time should be centered in left region
    return `
      <div class="absolute top-0 left-0 right-0 h-8 flex items-center">
        <!-- Time (left) - centered between left edge and Dynamic Island -->
        <div class="absolute left-0 top-1/2 -translate-y-1/2 flex items-center justify-center" style="width: calc((100% - 62px) / 2);">
          <span class="text-xs font-medium text-gray-900">9:41</span>
        </div>

        <!-- Dynamic Island (center) - scaled to iPhone 17 Pro proportions -->
        <div class="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2">
          <div class="bg-black rounded-full" style="width: 62px; height: 18px;"></div>
        </div>

        <!-- Status icons (right) - centered between Dynamic Island and right edge -->
        <div class="absolute right-0 top-1/2 -translate-y-1/2 flex items-center justify-center gap-0.5 overflow-visible" style="width: calc((100% - 62px) / 2);">
          <!-- Cellular signal bars - aligned with wifi height -->
          <svg class="text-gray-900" style="width: 14px; height: 11px;" viewBox="0 0 17 11" fill="currentColor">
            <rect x="0" y="8" width="3" height="3" rx="0.5"/>
            <rect x="4.5" y="5.5" width="3" height="5.5" rx="0.5"/>
            <rect x="9" y="2.75" width="3" height="8.25" rx="0.5"/>
            <rect x="13.5" y="0" width="3" height="11" rx="0.5"/>
          </svg>
          <!-- WiFi icon - concentric rings masked to 90° wedge -->
          <!-- Center at (0,0), height=15 (outer edge), width=30 (2*height for 90° angle) -->
          <!-- viewBox expanded by 5.26% to shrink content by 5% while keeping it centered -->
          <svg class="text-gray-900" style="width: 20px; height: 10px;" viewBox="-15.79 -15.395 31.58 15.79" fill="currentColor">
            <defs>
              <mask id="wifiWedge">
                <polygon points="0,0 -15,-15 15,-15" fill="white"/>
              </mask>
            </defs>
            <g mask="url(#wifiWedge)">
              <!-- Outer ring: r=13.5, stroke=3, outer edge at 15 -->
              <circle cx="0" cy="0" r="13.5" fill="none" stroke="currentColor" stroke-width="3"/>
              <!-- Middle ring: r=8.25, stroke=3, outer edge at 9.75 -->
              <circle cx="0" cy="0" r="8.25" fill="none" stroke="currentColor" stroke-width="3"/>
              <!-- Inner solid dot: r=4.5 -->
              <circle cx="0" cy="0" r="4.5" fill="currentColor"/>
            </g>
          </svg>
          <!-- Battery - aligned with wifi height -->
          <!-- viewBox starts at x=3 to crop empty left space, width=24 (from 3 to 27) -->
          <svg class="text-gray-900" style="width: 19.5px; height: 11px;" viewBox="3 0 24 11" fill="currentColor">
            <rect x="3.5" y="0.5" width="20" height="10" rx="2.5" stroke="currentColor" stroke-width="1" fill="currentColor"/>
            <text x="13.5" y="6.5" text-anchor="middle" dominant-baseline="middle" fill="white" font-size="9" font-weight="500">100</text>
            <path d="M24.5 3.5v4a2 2 0 000-4z"/>
          </svg>
        </div>
      </div>
    `;
  },

  /**
   * Generate the sticky header for inside scroll content
   */
  generateStickyHeader: function(companyName?: string): string {
    const displayName = companyName || 'Brand';
    return `
      <div class="sticky top-0 flex flex-col items-center pt-1 -mb-2">
        <div class="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center shadow-md z-10">
          <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
          </svg>
        </div>
        <p class="font-semibold text-gray-900 text-sm bg-gray-50/90 border border-white px-3 py-0.5 rounded-xl -mt-1 shadow-sm text-center max-w-[232px]">${this.escapeHtml(displayName)}</p>
      </div>
    `;
  },

  /**
   * Generate the phone input area
   */
  generatePhoneInputArea: function(): string {
    return `
      <div class="absolute bottom-3 left-0 right-0 h-[36px] flex items-center px-3">
        <div class="flex-1 bg-gray-50/90 border border-white rounded-full pl-3 pr-1.5 py-1 flex items-center justify-between shadow-sm">
          <span class="text-gray-400 text-sm">Message</span>
          <div class="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
            <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"/>
            </svg>
          </div>
        </div>
      </div>
    `;
  },

  /**
   * Generate the phone frame wrapper with content
   */
  generatePhoneFrame: function(contentHtml: string, companyName?: string): string {
    return `
      <div class="relative" style="width: 280px;">
        <!-- Phone frame -->
        <div class="bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
          <!-- Screen area (256x557 = iPhone 17 Pro aspect ratio 2.174:1) -->
          <div class="bg-white rounded-[2.25rem] overflow-hidden relative" style="height: 557px;">
            <!-- Content area (first so it's at bottom of stack) -->
            ${contentHtml}

            <!-- Gradient overlay beneath status bar -->
            <div class="absolute top-0 left-0 right-0 bg-gradient-to-b from-white/80 to-transparent" style="height: 40px;"></div>

            <!-- Gradient overlay above input area -->
            <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-b from-transparent to-white/80" style="height: 48px;"></div>

            <!-- Status bar (on top of content) -->
            ${this.generatePhoneStatusBar()}

            <!-- Input area (on top of content) -->
            ${this.generatePhoneInputArea()}
          </div>
        </div>
      </div>
    `;
  },

  /**
   * Generate the iPhone mockup frame with messages
   */
  generatePhoneMockup: function(messagesHtml: string, companyName?: string, showPartialAccessCTA: boolean = false): string {
    const ctaHtml = showPartialAccessCTA ? this.generatePartialAccessCTA() : '';
    const contentHtml = `
      <div class="absolute top-0 left-0 right-0 bottom-0 overflow-y-auto px-3" style="padding-top: 32px; padding-bottom: 51px;">
        ${this.generateStickyHeader(companyName)}
        ${messagesHtml}
        ${ctaHtml}
      </div>
    `;
    return this.generatePhoneFrame(contentHtml, companyName);
  },

  /**
   * Generate error content for missing/invalid demo data
   * Still shows programmatic and static stats when mermaid diagram is available
   */
  generateError: function(errorConfig: ErrorConfig = {}, mermaidDiagram: string | null = null, companyName?: string, productName?: string): string {
    const {
      title = 'Preview Unavailable',
      message = 'The journey preview could not be loaded.',
      details = ''
    } = errorConfig;

    // Compute programmatic stats from mermaid diagram (no conversation)
    const programmaticStats = computeStats(mermaidDiagram, null);

    // Merge: programmatic stats first, then static stats (no AI stats without demo data)
    const allStats = [...programmaticStats, ...this.getStaticStats()];
    const statsHtml = this.generateStatsChips(allStats);
    const phoneMockupHtml = this.generatePhoneMockupWithError(title, message, details, companyName);

    return this.generateLayout(statsHtml, phoneMockupHtml, productName);
  },

  /**
   * Generate phone mockup displaying an error
   */
  generatePhoneMockupWithError: function(title: string, message: string, details: string, companyName?: string): string {
    const errorHtml = window.errorDisplay
      ? window.errorDisplay.createComponentError({ title, message, details, showButton: false })
      : `<div class="p-4 text-red-600">${title}: ${message}</div>`;

    const contentHtml = `
      <div class="absolute top-0 left-0 right-0 bottom-0 overflow-y-auto px-3" style="padding-top: 32px; padding-bottom: 51px;">
        ${this.generateStickyHeader(companyName)}
        <div class="flex items-center justify-center px-1" style="min-height: 300px;">
          ${errorHtml}
        </div>
      </div>
    `;
    return this.generatePhoneFrame(contentHtml, companyName);
  },

  /**
   * Escape HTML special characters
   */
  escapeHtml: function(text: string): string {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  },

  /**
   * Linkify URLs and emails in text (call after escapeHtml)
   * @param text - The escaped HTML text
   * @param isBrandMessage - Whether this is a brand (outbound) message
   */
  linkify: function(text: string, isBrandMessage: boolean = true): string {
    if (!text) return '';

    // Brand messages: blue links. Customer messages: white links
    const linkClasses = isBrandMessage
      ? 'underline text-blue-500'
      : 'underline';

    // URL pattern (http, https)
    const urlPattern = /(https?:\/\/[^\s<]+)/g;

    // Email pattern
    const emailPattern = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;

    // Replace URLs with links
    text = text.replace(urlPattern, `<a href="$1" target="_blank" rel="noopener noreferrer" class="${linkClasses}">$1</a>`);

    // Replace emails with mailto links
    text = text.replace(emailPattern, `<a href="mailto:$1" target="_blank" rel="noopener noreferrer" class="${linkClasses}">$1</a>`);

    return text;
  },

  /**
   * Renders the phone demo content in the specified container
   */
  render: function(
    containerId: string,
    demoData: DemoData | null,
    metadata: DemoMetadata | null,
    mermaidDiagram: string | null,
    companyName?: string,
    productName?: string
  ): void {
    const container = document.getElementById(containerId);
    if (!container) {
      console.error(`Container with ID "${containerId}" not found`);
      return;
    }

    // Check if we have valid demo data
    const hasValidData = demoData &&
      demoData.conversation &&
      Array.isArray(demoData.conversation) &&
      demoData.conversation.length > 0;

    // Check validation status from metadata
    const isValid = metadata?.demoData?.content?.is_valid ?? hasValidData;
    const errors = metadata?.demoData?.content?.errors || [];

    // Check access level (default to full access if not specified)
    const isFullAccess = metadata?.access?.isFullAccess ?? true;

    if (!hasValidData) {
      container.innerHTML = this.generateError({
        title: 'Preview Unavailable',
        message: 'No journey preview data available for this product.',
        details: errors.length > 0 ? errors.join(', ') : 'Demo data was not generated'
      }, mermaidDiagram, companyName, productName);
    } else if (!isValid && errors.length > 0) {
      // Data exists but has validation errors - still try to show it
      container.innerHTML = this.generate(demoData, mermaidDiagram, companyName, isFullAccess, productName);
      console.warn('Demo data has validation warnings:', errors);
    } else {
      container.innerHTML = this.generate(demoData, mermaidDiagram, companyName, isFullAccess, productName);
    }

    // Use shared vertical centering utility if available
    if (window.verticalCenteringUtils) {
      window.verticalCenteringUtils.setupVerticalCentering(containerId, 'Phone Demo');
    }
  }
};

// Make available globally
(window as Window).phoneDemoContent = phoneDemoContent;
